import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Mail, Loader2, Paintbrush } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import EmailTemplateEditor from "../EmailTemplateEditor";

// datetime-local necesita "YYYY-MM-DDTHH:mm" en hora LOCAL, no ISO/UTC —
// toISOString() desplazaría la hora mostrada según el huso del navegador.
const toLocalInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EmailCampaignModal = ({ open, onClose, campaign, lists, onSave }) => {
  const initialState = {
    name: "",
    subject: "",
    bodyHtml: "",
    designJson: "",
    scheduledAt: "",
    listIds: [],
  };

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (campaign) {
      setForm({
        name: campaign.name || "",
        subject: campaign.subject || "",
        bodyHtml: campaign.bodyHtml || "",
        designJson: campaign.designJson || "",
        scheduledAt: toLocalInputValue(campaign.scheduledAt),
        listIds: (campaign.lists || []).map((l) => l.id),
      });
    } else {
      setForm(initialState);
    }
    setEditorOpen(false);
  }, [campaign, open]);

  const handleClose = () => {
    setForm(initialState);
    onClose();
  };

  const toggleList = (listId) => {
    setForm((prev) => ({
      ...prev,
      listIds: prev.listIds.includes(listId)
        ? prev.listIds.filter((id) => id !== listId)
        : [...prev.listIds, listId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warn("Ingresa un nombre para la campaña");
      return;
    }
    if (!form.subject.trim()) {
      toast.warn("Ingresa el asunto del correo");
      return;
    }
    if (!form.bodyHtml.trim()) {
      toast.warn("Diseña el contenido del correo con el editor visual");
      return;
    }
    if (form.listIds.length === 0) {
      toast.warn("Selecciona al menos una lista de destinatarios");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        subject: form.subject,
        bodyHtml: form.bodyHtml,
        designJson: form.designJson || null,
        scheduledAt: form.scheduledAt
          ? new Date(form.scheduledAt).toISOString()
          : null,
        listIds: form.listIds,
      };

      if (campaign) {
        await api.put(`/email-marketing/campaigns/${campaign.id}`, payload);
        toast.success("Campaña actualizada");
      } else {
        await api.post("/email-marketing/campaigns", payload);
        toast.success("Campaña creada como borrador");
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  if (!open) return null;

  // Una vez que la campaña dejó de ser borrador, ya no se puede editar (los
  // destinatarios ya se materializaron con ese contenido) — ver
  // EmailCampaignService.UpdateCampaignService en el backend.
  const readOnly = campaign && campaign.status !== "draft";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-600" />
            {campaign ? (readOnly ? "Ver Campaña" : "Editar Campaña") : "Nueva Campaña"}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30 overflow-y-auto flex-1">
          {readOnly && (
            <p className="text-xs text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
              Esta campaña ya se activó y no se puede editar.
            </p>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Nombre interno
            </label>
            <input
              type="text"
              value={form.name}
              disabled={readOnly}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              autoFocus
              placeholder="Ej: Promo de agosto"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Asunto
            </label>
            <input
              type="text"
              value={form.subject}
              disabled={readOnly}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, subject: e.target.value }))
              }
              placeholder="Lo que ve el destinatario en su bandeja de entrada"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Contenido — usa {"{{name}}"} para el nombre del contacto
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setEditorOpen(true)}
                  className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  <Paintbrush className="w-3.5 h-3.5" />
                  {form.bodyHtml.trim() ? "Editar diseño" : "Abrir editor visual"}
                </button>
              )}
            </div>
            {form.bodyHtml.trim() ? (
              <iframe
                title="preview"
                srcDoc={form.bodyHtml}
                sandbox=""
                className="w-full h-56 border border-slate-200 dark:border-slate-700 rounded-lg bg-white"
              />
            ) : (
              <button
                type="button"
                disabled={readOnly}
                onClick={() => setEditorOpen(true)}
                className="w-full h-32 flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-slate-400 hover:border-indigo-400 hover:text-indigo-500 disabled:opacity-60 disabled:hover:border-slate-200 disabled:hover:text-slate-400 transition-colors"
              >
                <Paintbrush className="w-5 h-5" />
                <span className="text-sm font-medium">
                  Diseñar el correo con el editor visual
                </span>
              </button>
            )}
            <p className="text-xs text-slate-400 mt-1">
              El pie de baja se añade automáticamente al enviar (configurable en la pestaña Configuración).
            </p>
          </div>

          <EmailTemplateEditor
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            initialHtml={form.bodyHtml}
            initialDesignJson={form.designJson}
            onSave={({ html, designJson }) => {
              setForm((prev) => ({ ...prev, bodyHtml: html, designJson }));
              setEditorOpen(false);
            }}
          />

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Enviar el (opcional — vacío = en cuanto se active)
            </label>
            <input
              type="datetime-local"
              value={form.scheduledAt}
              disabled={readOnly}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, scheduledAt: e.target.value }))
              }
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 disabled:opacity-60"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Listas destinatarias
            </label>
            {lists.length === 0 ? (
              <p className="text-sm text-slate-400">
                No hay listas todavía — crea una en la pestaña "Listas".
              </p>
            ) : (
              <div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg p-2 bg-white dark:bg-slate-800">
                {lists.map((list) => (
                  <label
                    key={list.id}
                    className="flex items-center gap-2 px-1 py-1 text-sm text-slate-700 dark:text-slate-300"
                  >
                    <input
                      type="checkbox"
                      checked={form.listIds.includes(list.id)}
                      disabled={readOnly}
                      onChange={() => toggleList(list.id)}
                    />
                    {list.name}{" "}
                    <span className="text-xs text-slate-400">
                      ({list.memberCount || 0})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            {readOnly ? "Cerrar" : "Cancelar"}
          </button>
          {!readOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {campaign ? "Guardar" : "Crear borrador"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignModal;
